import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

import { SERVER_API_URL } from '../../app.constants';
import { clearKgChatStorage } from '../../knowledge-graph/auth-token';

@Injectable({ providedIn: 'root' })
export class AuthServerProvider {
  private refreshUrl = `${window.location.origin}/auth/refresh`;
  private readonly ACCESS_TOKEN_KEY = 'authToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';

  constructor(
    private router: Router,
    private http: HttpClient
  ) {}

  login(credentials: { username: string; password: string }): Observable<any> {
    const data =
      `username=${encodeURIComponent(credentials.username)}` +
      `&password=${encodeURIComponent(credentials.password)}` +
      `&submit=Login`;
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post(SERVER_API_URL + 'api/authentication', data, { headers });
  }

  // KCS-565: candidate fallback login with username (the fake email) + password, used when the QR
  // camera is unavailable. Hits Spring directly (never the .NET/LDAP service).
  // Returns { isAuthenticated, token, refreshToken, user }.
  loginByCredentials(username: string, password: string): Observable<any> {
    return this.http.post(SERVER_API_URL + 'api/authentication/credentials', { username, password });
  }

  // KCS-565 (Task 3b): step 1 of QR login — validate the scanned token, return identity for the
  // "Log in as <name>?" confirmation (no tokens minted). Returns { valid, name, phone, login }.
  qrResolve(token: string): Observable<any> {
    return this.http.post(SERVER_API_URL + 'api/authentication/qr/resolve', { token });
  }

  // KCS-565 (Task 3b): step 2 — mint the access+refresh pair for the scanned candidate.
  // Returns { isAuthenticated, token, refreshToken, user } (same shape as phone/phone-password).
  qrConfirm(token: string): Observable<any> {
    return this.http.post(SERVER_API_URL + 'api/authentication/qr/confirm', { token });
  }

  logout(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    clearKgChatStorage();
    this.router.navigate(['/login']);
  }

  refreshToken(refreshToken: string): Observable<any> {
    return this.http.post(SERVER_API_URL + 'auth/refresh', {
      refreshToken
    });
  }

  getAccessToken(): string | null {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (token && this.isTokenExpired(token)) {
      // Access token expired — drop only it and KEEP the refresh token, so the auth-expired
      // interceptor can exchange the refresh token for a new pair on the next 401. (Previously this
      // cleared BOTH tokens, which forced a re-login on every expiry — the refresh token was gone
      // before the interceptor could use it.)
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      return null;
    }
    return token;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwtDecode(token);
      if (!decoded.exp) return false;
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}
